import { ISize } from '@renderforest/webwood-engine'
import { IMediaItem } from 'types/media'
import { getInitialDimensions } from 'utils'

/**
 * Derives the canvas dimensions for the "Original" aspect ratio from the media
 * on the timeline: the largest width/height across all source-based (non-text)
 * items. Returns `null` when there is no usable source media yet, so callers can
 * fall back to a fixed ratio. Preserves each item's native ratio because
 * `meta.originalWidth/Height` keep the source aspect ratio (see
 * `convertBaseItemToMediaItem`).
 */
export const computeOriginalDimensions = (
  mediaItems: IMediaItem[]
): ISize | null => {
  let maxW = 0
  let maxH = 0
  const sourceBasedItems = mediaItems.filter(item => item.type !== 'text')
  for (const item of sourceBasedItems) {
    const w =
      item.type === 'text'
        ? item.width
        : (item.meta.originalWidth ?? item.width ?? 0)
    const h =
      item.type === 'text'
        ? item.height
        : (item.meta.originalHeight ?? item.height ?? 0)
    if (w > maxW) maxW = w
    if (h > maxH) maxH = h
  }
  if (maxW <= 0 || maxH <= 0) return null
  return { width: Math.floor(maxW), height: Math.floor(maxH) }
}

/**
 * Rescales computed "Original" dimensions so the smallest side matches the
 * bootstrap canvas' smallest side, keeping canvas resolution in a consistent
 * range regardless of the source media's absolute pixel size.
 */
export const normalizeDimensions = (dimensions: ISize | null): ISize | null => {
  if (!dimensions) {
    return dimensions
  }
  const currentSmallestSize = Math.min(dimensions.width, dimensions.height)
  const initialDimensions = getInitialDimensions()
  const initialSmallestSize = Math.min(
    initialDimensions.width,
    initialDimensions.height
  )
  const normalizationMultiplier = initialSmallestSize / currentSmallestSize
  return {
    width: Math.round(dimensions.width * normalizationMultiplier),
    height: Math.round(dimensions.height * normalizationMultiplier)
  }
}
